import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { CalendarDays, MapPin, Users, FileText, Clock, PartyPopper, Anchor, Mail, Phone, ShieldCheck } from "lucide-react";
import { api, IMAGES } from "@/lib/api";
import { ReviewsSection } from "@/components/Reviews";

const FALLBACK_IMG = { party: IMAGES.cheers, stingray: IMAGES.stingray };

const TourCard = ({ testid, img, badge, badgeClass, title, price, priceNote, details, bookLink, bookTestId }) => (
  <div data-testid={testid} className="tour-card bg-white rounded-3xl overflow-hidden border border-cyan-100 shadow-xl">
    <div className="relative h-64">
      <img src={img} alt={title} className="w-full h-full object-cover" />
      <span className={`absolute top-4 left-4 text-xs font-bold px-3 py-1.5 rounded-full ${badgeClass}`}>{badge}</span>
    </div>
    <div className="p-6 sm:p-8">
      <div className="flex items-start justify-between gap-4 mb-4">
        <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-900">{title}</h3>
        <div className="text-right shrink-0">
          <p className="font-mono-code text-2xl font-bold text-sky-600">{price}</p>
          <p className="text-xs text-slate-500">{priceNote}</p>
        </div>
      </div>
      <ul className="space-y-2.5 mb-6">
        {details.map(([Icon, text], i) => (
          <li key={i} className="flex items-center gap-2.5 text-sm text-slate-600">
            <Icon size={16} className="text-cyan-500 shrink-0" /> {text}
          </li>
        ))}
      </ul>
      <Link to={bookLink} data-testid={bookTestId} className="btn-ocean block text-center text-white font-bold py-3 rounded-full">
        Book This Tour
      </Link>
    </div>
  </div>
);

export default function Home() {
  const { hash } = useLocation();
  const [tours, setTours] = useState([]);
  useEffect(() => {
    api.get("/tours").then((r) => setTours(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [hash]);
  return (
    <div className="bg-[#f5fbfd]">
      <section className="relative min-h-[92vh] flex items-end">
        <img src={IMAGES.water} alt="Turquoise Cayman water" className="absolute inset-0 w-full h-full object-cover" />
        <div className="hero-scrim absolute inset-0" />
        <img
          src={IMAGES.heroLogo}
          alt="Mickey Mouse Charters logo"
          data-testid="hero-logo-overlay"
          className="absolute z-10 fade-up left-1/2 -translate-x-1/2 top-24 w-44 sm:w-56 lg:left-auto lg:translate-x-0 lg:right-16 lg:top-1/2 lg:-translate-y-1/2 lg:w-[22rem] drop-shadow-[0_10px_30px_rgba(0,0,0,0.5)] pointer-events-none"
        />
        <div className="relative max-w-6xl mx-auto w-full px-4 sm:px-8 pb-20 pt-36">
          <div className="max-w-2xl fade-up">
            <p className="text-cyan-300 font-semibold tracking-[0.25em] uppercase text-xs mb-4">
              Cayman Islands · Watersports Charters
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-none tracking-tight mb-5">
              Experience <span className="text-cyan-400">Paradise.</span>
              <br />Party on the <span className="text-orange-400">Water.</span>
            </h1>
            <p className="text-slate-200 text-base sm:text-lg mb-8 max-w-xl">
              All-inclusive party cruises and unforgettable beach getaways in the crystal-clear waters of Grand Cayman.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/book?tour=party" data-testid="hero-cta-party-cruise" className="btn-coral text-white font-bold px-7 py-3.5 rounded-full">
                Book Party Cruise
              </Link>
              <Link to="/book?tour=stingray" data-testid="hero-cta-stingray-tour" className="bg-white/10 backdrop-blur border border-white/30 text-white font-bold px-7 py-3.5 rounded-full hover:bg-white/20 transition-colors duration-150">
                Beach Getaway
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-16 lg:py-24">
        <p className="text-cyan-600 font-semibold tracking-[0.2em] uppercase text-xs mb-3">Our Experiences</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-10">
          Two ways to make waves
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {tours.map((t) => {
            const [daysPart, timesPart] = (t.schedule || "").split(" · ");
            return (
              <TourCard
                key={t.id}
                testid={`tour-card-${t.id === "stingray" ? "stingray-tour" : t.id === "party" ? "party-cruise" : t.id}`}
                img={t.image || FALLBACK_IMG[t.id] || IMAGES.water}
                badge={t.min_age > 0 ? `${t.min_age}+ Only` : "All Ages Welcome"}
                badgeClass={t.min_age > 0 ? "bg-orange-500 text-white" : "bg-cyan-500 text-white"}
                title={t.name}
                price={t.price_child != null ? `$${t.price_adult} / $${t.price_child}` : `$${t.price_adult}`}
                priceNote={t.price_child != null ? "adults / kids under 18" : "per person"}
                details={[
                  [CalendarDays, daysPart || t.schedule],
                  [Clock, timesPart || "See availability"],
                  [MapPin, t.boarding || t.location],
                  [Users, `${t.min_group > 1 ? `Minimum ${t.min_group} guests · ` : ""}up to ${t.capacity} guests`],
                  t.min_age > 0 ? [ShieldCheck, `Adults only — minimum age ${t.min_age}`] : [PartyPopper, "Family favorite — no minimum age"],
                  [FileText, "Waiver signed at boarding"],
                ]}
                bookLink={`/book?tour=${t.id}`}
                bookTestId={`book-tour-button-${t.id}`}
              />
            );
          })}
        </div>
      </section>

      <section className="bg-[#071426] py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-10">Life on board</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <img src={IMAGES.boat} alt="On the charter boat" className="rounded-2xl h-72 w-full object-cover" />
            <img src={IMAGES.water} alt="Guests in turquoise water" className="rounded-2xl h-72 w-full object-cover" />
            <img src={IMAGES.cheers} alt="Cheers on deck" className="rounded-2xl h-72 w-full object-cover" />
          </div>
        </div>
      </section>

      <section data-testid="about-us-section" className="max-w-6xl mx-auto px-4 sm:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-cyan-600 font-semibold tracking-[0.2em] uppercase text-xs mb-3">About Us</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              25 years on Cayman's waters
            </h2>
            <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
              <p>
                With over 25 years of experience in the watersports and excursion business in the Cayman Islands,
                we've had the privilege of welcoming guests from around the world and sharing the beauty of our
                incredible waters with them.
              </p>
              <p>
                For us, it's about more than simply providing an excursion — it's about creating great experiences
                and making sure every day on the water is one to remember. From exploring the crystal-clear
                Caribbean waters to enjoying unforgettable beach getaways, we take pride in making every guest feel
                welcome, comfortable, and well taken care of.
              </p>
              <p>
                After 25 years, our passion remains the same: great service, unforgettable experiences, and happy
                guests. We look forward to showing you the best of Cayman from the water!
              </p>
            </div>
          </div>
          <div className="relative">
            <img src={IMAGES.boat} alt="Aboard a Mickey Mouse Charters boat" className="rounded-3xl w-full h-[420px] object-cover shadow-2xl" />
            <div className="absolute -bottom-5 -left-3 sm:-left-5 bg-black text-white rounded-2xl px-6 py-4 shadow-xl flex items-center gap-3">
              <Anchor size={22} className="text-cyan-400" />
              <div>
                <p className="font-display font-extrabold text-lg leading-none">25+ Years</p>
                <p className="text-xs text-slate-300 mt-1">of watersports experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ReviewsSection />

      <section id="contact" data-testid="contact-us-section" className="bg-black py-16 lg:py-20 scroll-mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <p className="text-cyan-400 font-semibold tracking-[0.2em] uppercase text-xs mb-3">Contact Us</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-8">
            Questions? We'd love to hear from you
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-5">
            <a
              href="mailto:admin@mmcharters.ky"
              data-testid="contact-email-link"
              className="flex items-center justify-center gap-3 bg-white/5 border border-white/15 rounded-2xl px-8 py-5 hover:border-cyan-400 transition-colors duration-150"
            >
              <Mail size={20} className="text-cyan-400" />
              <span className="text-white font-bold">admin@mmcharters.ky</span>
            </a>
            <a
              href="tel:+13459161163"
              data-testid="contact-phone-link"
              className="flex items-center justify-center gap-3 bg-white/5 border border-white/15 rounded-2xl px-8 py-5 hover:border-cyan-400 transition-colors duration-150"
            >
              <Phone size={20} className="text-cyan-400" />
              <span className="text-white font-bold">1 (345) 916-1163</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
